import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { colors } from "../../utils";
import Button from "../Button";
import EnvelopeLineArt from "../../assets/icons/EnvelopeLineArt";
import { useResendVerificationLink } from "../../services/mutations";
import { EMAIL_UNVERIFIED_CUE_EVENT } from "../../utils/emailUnverifiedCue";

// ISP-9999 fe-unverified-banner-and-cues. Thin, non-blocking top strip
// (contrast with the existing EmailVerificationPopup, which is a blocking
// auth-flow modal, not a persistent app banner) telling an authenticated-but
// -unverified owner their gated actions (purchase, publish/share, chat send,
// invite, email change, referral, username set, clone) will be refused until
// they confirm their email, with a one-tap resend + cooldown countdown.
// Existing tokens only — no new colour.
const Bar = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 16px;
  background: ${colors.orange};
  border-bottom: 1px solid ${colors.lightBorder};
  font-family: "DM Sans", sans-serif;
  transition: box-shadow 0.3s ease;

  &.pulsing {
    box-shadow: inset 0 0 0 2px ${colors.PrimaryPurple500};
  }
`;

const Text = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13.5px;
  font-weight: 500;
  color: ${colors.primary};
`;

const CountdownText = styled.span`
  font-size: 12.5px;
  color: ${colors.darkGrey};
  white-space: nowrap;
`;

// The backend's per-address resend rate limit starts at a 1-minute cooldown
// after the first send (see backend users/services/resend_rate_limit.py
// RESEND_MINUTE_TTL_SECONDS). A successful resend doesn't itself report
// seconds_remaining (only a 429 refusal does), so we start the countdown
// optimistically at that same value and let an authoritative 429
// seconds_remaining (if the user manages to click again mid-cooldown)
// resync it.
export const OPTIMISTIC_COOLDOWN_SECONDS = 60;

const UnverifiedBanner = () => {
  const profile = useSelector((state) => state.userProfile?.userProfile);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [pulsing, setPulsing] = useState(false);
  const resendMutation = useResendVerificationLink();
  const pulseTimeoutRef = useRef(null);

  useEffect(() => {
    if (secondsRemaining <= 0) return undefined;
    const id = setInterval(() => {
      setSecondsRemaining((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [secondsRemaining]);

  useEffect(() => {
    const onCue = () => {
      setPulsing(true);
      clearTimeout(pulseTimeoutRef.current);
      pulseTimeoutRef.current = setTimeout(() => setPulsing(false), 1500);
    };
    window.addEventListener(EMAIL_UNVERIFIED_CUE_EVENT, onCue);
    return () => {
      window.removeEventListener(EMAIL_UNVERIFIED_CUE_EVENT, onCue);
      clearTimeout(pulseTimeoutRef.current);
    };
  }, []);

  const isUnverified = profile?.is_email_verified === false;
  if (!isUnverified) return null;

  const handleResend = () => {
    resendMutation.mutate(
      { email: profile.email },
      {
        onSuccess: () => setSecondsRemaining(OPTIMISTIC_COOLDOWN_SECONDS),
        onError: (error) => {
          const authoritative = error?.response?.data?.seconds_remaining;
          setSecondsRemaining(
            typeof authoritative === "number"
              ? authoritative
              : OPTIMISTIC_COOLDOWN_SECONDS
          );
        },
      }
    );
  };

  const counting = secondsRemaining > 0;

  return (
    <Bar
      role="status"
      aria-live="polite"
      className={pulsing ? "pulsing" : ""}
      data-testid="unverified-banner"
    >
      <EnvelopeLineArt />
      <Text>Confirm your email to unlock everything</Text>
      {counting ? (
        <CountdownText data-testid="resend-countdown">
          Resend available in {secondsRemaining}s
        </CountdownText>
      ) : (
        <Button
          width="auto"
          style={{ width: "auto", flex: "0 0 auto", minHeight: 32 }}
          isLoading={resendMutation.isLoading}
          onClick={handleResend}
        >
          Resend
        </Button>
      )}
    </Bar>
  );
};

export default UnverifiedBanner;
